import * as React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import * as campaignActions from 'actions/campaign';
import * as routerActions from 'router/actions';
import { useI18n } from 'components/I18n';
import {
  OFFER_TYPES,
  PURCHASE_FLOW_OFFER_TYPES,
} from 'utils/constants';
import {
  PaymentProviderFooter,
  AdyenResultHandler,
  SkipOnboardingButton,
} from 'components/Payment';
import {
  Box,
  Heading,
  Text,
  Metadata,
  SecondaryButton,
  PrimaryButton,
} from 'components';
import { useQuery } from '@apollo/client';
import NoOffersErrorPage from 'components/ErrorPage/NoOffersErrorPage';
import { Offer } from 'types';
import { RouterLocation } from 'router';
import { AppDispatch, RootState } from 'reducers';
import { flattenEdges } from 'utils/helpers';
import * as OFFERS_ENTITLEMENTS_QUERY from 'views/Checkout/offersAndEntitlements.gql';
import ErrorPage, { ERROR_ICONS } from 'components/ErrorPage/ErrorPage';
import PromoCodeModal from '../PromoCodeModal';
import PromocodeDifferentOfferModal from '../PromocodeDifferentOffer';
import { normalizePromoCode } from 'views/Checkout/utils';
import { MSG_IDS, getOffer, mostExpensiveOffer } from './utils';
import OfferComponent from './Offer';
import { Container } from './styles';
import logger from 'utils/logger';

type Props = {
  location: RouterLocation,
};

// Normal Offer selection component as part of SignUp (also can be opened from My Plan)
// Route: '/checkout'
function OfferSelectionView({ location }: Props) {
  const i18n = useI18n();
  const dispatch = useDispatch<AppDispatch>();

  const {
    promoCode: rawPromoCode,
    createAccount,
    redirectTo,
  } = location.query!;
  const promoCode = normalizePromoCode(rawPromoCode);

  const {
    data,
    error: apolloError,
  } = useQuery(OFFERS_ENTITLEMENTS_QUERY);

  const {
    withPromoCodes,
  } = useSelector((state: RootState) => ({
    withPromoCodes: state.settings.features.payment?.allowPromoCodes || false,
  }), shallowEqual);

  const [selectedOfferId, setSelectedOfferId] = React.useState<string | undefined>();
  const [bestValueOfferId, setBestValueOfferId] = React.useState<string | undefined>();
  const [shouldFocusContinue, setShouldFocusContinue] = React.useState(false);
  const continueCmdRef = React.useRef<HTMLButtonElement>(null);

  const [showModal, setShowModal] = React.useState(!!promoCode);
  const [confirmModalOffer, setConfirmModalOffer] = React.useState<undefined | string>();
  const [confirmModalPromo, setConfirmModalPromo] = React.useState<undefined | string>();
  const [initPromoValidation, setInitPromoValidation] = React.useState(!!promoCode);

  const entitlements = flattenEdges(data?.viewer?.entitlements || []);
  const availableOffers = flattenEdges(data?.viewer?.offers || [])
    // filter out purchased offers
    .filter((offer: Offer) => !entitlements.includes(offer.id))
    // filter out unsupported offer types
    // @ts-expect-error ts-migrate(2345) FIXME
    .filter((offer: Offer) => PURCHASE_FLOW_OFFER_TYPES.includes(offer.__typename));

  const validatePromocode = async (newPromoCode: string) => {
    const expectedOfferIds = availableOffers.map((offer: Offer) => offer.id);

    try {
      const { offerIds } = await dispatch(campaignActions.getCampaign(newPromoCode));

      const matchingOfferIds = expectedOfferIds.filter((id: string) => offerIds.includes(id));

      if (!matchingOfferIds.length) {
        throw { errorId: 'promoCode.notValid' };
      }

      const offerId = matchingOfferIds[0];

      const { valid, errorId } = await dispatch(campaignActions.validatePromocodeToOffer(offerId, newPromoCode));

      if (!valid) {
        const error = errorId === 'promoCode.notValidForOffer' ? 'promoCode.notValid' : errorId;
        throw { errorId: error };
      }

      if (selectedOfferId && selectedOfferId !== offerId) {
        setShowModal(false);
        setConfirmModalOffer(offerId);
        setConfirmModalPromo(newPromoCode);
      } else {
        dispatch(routerActions.push({
          name: 'checkout-confirmation',
          query: {
            createAccount,
            promoCode: newPromoCode,
            packageId: offerId,
            redirectTo,
          },
        }));
      }
    } catch (e: any) {
      setInitPromoValidation(false);
      throw i18n.formatText(
        e?.errorId || campaignActions.extractPromocodeValidationError(JSON.stringify(e)),
        { promoCode: newPromoCode },
      );
    }
  };

  React.useEffect(() => {
    if (
      promoCode
      && withPromoCodes
      && availableOffers?.length > 1 // if 1 > auto redirect
    ) {
      void validatePromocode(promoCode as string).catch((e)=> {
        logger.warn('Promocode validation failed', e);
      });
    }

    // auto select
    const mostExpensive = mostExpensiveOffer(availableOffers.sort((a: any, b: any) => {
      return PURCHASE_FLOW_OFFER_TYPES.indexOf(a.__typename) - PURCHASE_FLOW_OFFER_TYPES.indexOf(b.__typename);
    }));
    if (mostExpensive) {
      setSelectedOfferId(mostExpensive.id);
      if (mostExpensive.__typename === OFFER_TYPES.SubscribeType) {
        setBestValueOfferId(mostExpensive.id);
      }
    }
  }, [availableOffers.length]);

  React.useEffect(() => {
    if (shouldFocusContinue && selectedOfferId && continueCmdRef.current) {
      continueCmdRef.current.focus();
      setShouldFocusContinue(false);
    }
  }, [shouldFocusContinue, selectedOfferId]);

  const hideModal = () => {
    if (promoCode) { // remove promoCode from the url
      dispatch(routerActions.push({
        name: 'checkout',
        query: {
          createAccount,
          redirectTo,
        },
      }));
    }

    setShowModal(false);
  };

  const onOfferClick = (offerId: string) => {
    setSelectedOfferId(offerId);
  };

  const onKeyboardSelect = () => {
    setShouldFocusContinue(true);
  };

  const openPayment = (offerId: string, promo?: string) => {
    dispatch(routerActions.push({
      name: 'checkout-confirmation',
      query: {
        createAccount,
        packageId: offerId,
        redirectTo,
        ...( promo ? { promoCode: promo } : null ),
      },
    }));
  };

  if (apolloError) {
    return (
      <ErrorPage button="retry" icon={ERROR_ICONS.NOT_FOUND}>
        <Text id="error" />
      </ErrorPage>
    );
  }

  if (availableOffers.length === 1) {
    dispatch(routerActions.push({
      name: 'checkout-confirmation',
      query: {
        createAccount,
        promoCode,
        packageId: availableOffers[0].id,
        redirectTo,
      },
    }));
  }

  if (data && !availableOffers.length) {
    return (
      <NoOffersErrorPage hasEntitlement={!!entitlements.length} />
    );
  }

  if (!data || initPromoValidation) {
    return null;
  }

  const availableOfferTypes = PURCHASE_FLOW_OFFER_TYPES.filter(
    (offerType: string) => availableOffers
      .filter((item: Offer) => item.__typename === offerType).length > 0,
  );

  const items = availableOfferTypes.map((offerType: string) => {
    const offersOfType = availableOffers.filter((item: Offer) => item.__typename === offerType);

    return (
      <Container
        key={offerType}
        mb="xlarge"
        maxWidth="100%"
      >
        <Metadata title="metadata.checkout.title" />

        <Heading
          fontSize="sectionHeading"
          align="center"
          // @ts-expect-error ts-migrate(7053) FIXME
          id={MSG_IDS[offerType]}
          as="h2"
        />

        <Box xl-mt="xlarge" mt="large" row wrap columnGap="large">
          {offersOfType.map((offer: Offer) => (
            <OfferComponent
              key={offer.id}
              selected={offer.id === selectedOfferId}
              bestValue={offer.id === bestValueOfferId}
              offer={offer}
              onClick={() => onOfferClick(offer.id)}
              onKeyboardSelect={onKeyboardSelect}
            />
          ))}
        </Box>
      </Container>
    );
  });

  return (
    <>
    <Box column mx="auto" xl-mt="mlarge" mt="large" maxWidth="100%" width="100%">

      <Heading id="metadata.checkout.title" hidden />

      <Box mb="medium">
        <AdyenResultHandler />
      </Box>

      {items}

      {withPromoCodes && showModal && (
        <PromoCodeModal
          initialValue={promoCode?.toString()}
          validatePromocode={validatePromocode}
          onClose={hideModal}
        />
      )}

      {confirmModalOffer && (
        <PromocodeDifferentOfferModal
          onDismiss={() => setConfirmModalOffer(undefined)}
          onConfirm={() => openPayment(confirmModalOffer, confirmModalPromo)}
          offerName={getOffer(confirmModalOffer, availableOffers)?.title}
        />
      )}

      <Box mb="xxlarge">
        <SkipOnboardingButton />
      </Box>

      <Box mb="small">
        <PaymentProviderFooter />
      </Box>
    </Box>

    <Box
      flexBox
      p="medium"
      xl-p="large"
      position="sticky"
      left="0"
      right="0"
      bottom="0"
      xs-column
      xs-alignItems="stretch"
      justifyContent="center"
      gap="large"
      width="100%"
      bg="page"
      wrap
      borderTop="divider"
    >
      {withPromoCodes && (
        <SecondaryButton
          variant="white"
          onClick={() => setShowModal(true)}
          className="e2e-promocode-cta"
        >
          <Text id="payment.promoCodeButton" />
        </SecondaryButton>
      )}
      <PrimaryButton
        onClick={selectedOfferId ? () => openPayment(selectedOfferId) : undefined}
        disabled={!selectedOfferId}
        innerRef={continueCmdRef}
      >
        <Text id="changeOffer.continue" />
      </PrimaryButton>
    </Box>
    </>
  );
}

export default React.memo(OfferSelectionView);
